// The daily check-in (brief §6). Target: under 30 seconds on a routine day.
//
// Everything that makes that possible is a deliberate choice:
//   · one screen, no steps, no "next"
//   · sliders and chips, no typing unless the user wants to
//   · only the metrics this experiment declared required; extras behind a disclosure
//   · the date, experiment and units are never asked — the app already knows them
//   · a save button that is always reachable without scrolling back

import { useCallback, useEffect, useState } from 'react';
import { api, type CheckInForm, type CheckInResult } from '../lib/api.ts';
import {
  Button, Card, Chip, EmptyState, ErrorNote, ScaleInput, SectionTitle, Spinner, TextArea,
} from '../components/ui.tsx';
import { CONFOUNDER_LABELS, RED_FLAG_CHIPS, weekdayDate } from '../lib/format.ts';

type Value = number | string | boolean | null;

export function CheckIn({ experimentId, navigate }: { experimentId: string; navigate: (path: string) => void }) {
  const [form, setForm] = useState<CheckInForm | null>(null);
  const [values, setValues] = useState<Record<string, Value>>({});
  const [overall, setOverall] = useState<'better' | 'same' | 'worse' | null>(null);
  const [confounders, setConfounders] = useState<string[]>([]);
  const [sideEffects, setSideEffects] = useState<string[]>([]);
  const [redFlags, setRedFlags] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [showExtras, setShowExtras] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<CheckInResult | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.experiments.checkInForm(experimentId);
        setForm(data);
        setValues(data.existing as Record<string, Value>);
        setOverall(data.overall_status);
        setConfounders(data.confounders);
        setSideEffects(data.side_effects);
        setNotes(data.notes ?? '');
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [experimentId]);

  const toggle = useCallback((list: string[], set: (v: string[]) => void, item: string) => {
    set(list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);
  }, []);

  const save = async (): Promise<void> => {
    if (!form) return;
    setSaving(true);
    setError(null);
    try {
      const outcome = await api.experiments.submitCheckIn(experimentId, {
        local_date: form.local_date,
        values,
        overall_status: overall,
        notes: notes.trim() || null,
        confounders,
        side_effects: sideEffects,
        red_flag_symptoms: redFlags,
      });
      setResult(outcome);
      // A clean check-in returns to Today; one that raised a flag stays put so the
      // advice is actually read rather than flashing past on a redirect.
      if (outcome.flags.length === 0) navigate('/');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (error && !form) return <ErrorNote message={error} />;
  if (!form) return <Spinner />;

  if (form.metrics.length === 0) {
    return (
      <EmptyState
        title="No measures set up"
        detail="This experiment has nothing to record yet. Add a measure to it first."
        action={<Button onClick={() => navigate(`/experiments/${experimentId}`)}>Open experiment</Button>}
      />
    );
  }

  if (result && result.flags.length > 0) {
    return <CheckInEscalation result={result} navigate={navigate} />;
  }

  const required = form.metrics.filter((m) => m.required);
  const optional = form.metrics.filter((m) => !m.required);

  return (
    <div className="space-y-6 pb-4">
      <p className="text-sm text-muted -mt-1">{weekdayDate(form.local_date)}</p>

      <div className="space-y-4">
        {required.map((metric) => (
          <MetricInput key={metric.id} metric={metric} value={values[metric.id] ?? null}
            onChange={(v) => setValues((prev) => ({ ...prev, [metric.id]: v }))} />
        ))}
      </div>

      <section>
        <SectionTitle>Compared with yesterday</SectionTitle>
        <div className="grid grid-cols-3 gap-2">
          {(['better', 'same', 'worse'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={overall === option}
              onClick={() => setOverall(overall === option ? null : option)}
              className={`min-h-14 rounded-xl border text-sm font-medium capitalize
                ${overall === option ? 'bg-accent-soft border-accent' : 'bg-surface border-hairline text-ink-2'}`}
            >
              {overall === option ? '✓ ' : ''}{option}
            </button>
          ))}
        </div>
      </section>

      <section>
        <SectionTitle>Anything unusual today?</SectionTitle>
        <div className="flex flex-wrap gap-2">
          {Object.entries(CONFOUNDER_LABELS).map(([id, label]) => (
            <Chip key={id} label={label} selected={confounders.includes(id)}
              onClick={() => toggle(confounders, setConfounders, id)} />
          ))}
        </div>
      </section>

      {optional.length > 0 && (
        <section>
          <button
            type="button"
            onClick={() => setShowExtras(!showExtras)}
            className="text-sm text-accent min-h-11"
          >
            {showExtras ? 'Hide' : 'Add'} optional details ({optional.length})
          </button>
          {showExtras && (
            <div className="space-y-4 mt-2">
              {optional.map((metric) => (
                <MetricInput key={metric.id} metric={metric} value={values[metric.id] ?? null}
                  onChange={(v) => setValues((prev) => ({ ...prev, [metric.id]: v }))} />
              ))}
            </div>
          )}
        </section>
      )}

      <section>
        <SectionTitle>Side effects</SectionTitle>
        <div className="flex flex-wrap gap-2">
          {['Nose irritation', 'Dryness', 'Nosebleed', 'Unpleasant taste', 'Headache', 'Nausea', 'Bloating', 'Other']
            .map((name) => (
              <Chip key={name} label={name} selected={sideEffects.includes(name)}
                onClick={() => toggle(sideEffects, setSideEffects, name)} />
            ))}
        </div>
      </section>

      <section>
        <SectionTitle>Notes</SectionTitle>
        <TextArea value={notes} onChange={setNotes} rows={2}
          placeholder="Anything worth remembering about today (optional)" />
      </section>

      {/* Deliberately last and visually separate: these are not part of the routine
          rhythm, and putting them among the sliders would train people to skim them. */}
      <section>
        <details className="card p-4">
          <summary className="text-sm font-medium cursor-pointer min-h-11 flex items-center">
            Report a symptom that concerns you
          </summary>
          <p className="text-xs text-ink-2 mt-2 mb-3">
            Tick anything you have noticed today. These are checked against safety rules,
            not tracked as part of the experiment.
          </p>
          <div className="flex flex-wrap gap-2">
            {RED_FLAG_CHIPS.map((chip) => (
              <Chip key={chip.id} label={chip.label} tone="warning" selected={redFlags.includes(chip.id)}
                onClick={() => toggle(redFlags, setRedFlags, chip.id)} />
            ))}
          </div>
        </details>
      </section>

      {error && <ErrorNote message={error} />}

      <div className="sticky bottom-24 pt-2">
        <Button variant="primary" full disabled={saving} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save check-in'}
        </Button>
      </div>
    </div>
  );
}

function MetricInput({ metric, value, onChange }: {
  metric: CheckInForm['metrics'][number];
  value: Value;
  onChange: (v: Value) => void;
}) {
  const label = metric.prompt ?? metric.name;

  if (metric.type === 'scale' || metric.type === 'number') {
    const min = metric.min_value ?? 0;
    const max = metric.max_value ?? 10;
    return (
      <Card>
        <p className="text-sm font-medium mb-1">
          {label}
          {metric.unit && <span className="text-muted font-normal"> ({metric.unit})</span>}
        </p>
        <ScaleInput
          value={typeof value === 'number' ? value : null}
          onChange={onChange}
          min={min}
          max={max}
          lowLabel={metric.higher_is_better ? 'Worst' : 'None'}
          highLabel={metric.higher_is_better ? 'Best' : 'Worst'}
        />
      </Card>
    );
  }

  if (metric.type === 'boolean') {
    return (
      <Card>
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-medium">{label}</p>
          <div className="flex gap-2 shrink-0">
            <Chip label="No" selected={value === false} onClick={() => onChange(value === false ? null : false)} />
            <Chip label="Yes" selected={value === true} onClick={() => onChange(value === true ? null : true)} />
          </div>
        </div>
      </Card>
    );
  }

  if (metric.type === 'choice' && metric.choices) {
    return (
      <Card>
        <p className="text-sm font-medium mb-2">{label}</p>
        <div className="flex flex-wrap gap-2">
          {metric.choices.map((choice) => (
            <Chip key={choice} label={choice} selected={value === choice}
              onClick={() => onChange(value === choice ? null : choice)} />
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <p className="text-sm font-medium mb-2">{label}</p>
      <input
        type={metric.type === 'time' ? 'time' : 'text'}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full min-h-11 px-3 rounded-xl bg-surface border border-hairline"
      />
    </Card>
  );
}

/** Shown after a check-in that raised a flag, instead of bouncing back to Today. */
function CheckInEscalation({ result, navigate }: { result: CheckInResult; navigate: (path: string) => void }) {
  const urgent = result.level === 'urgent';
  return (
    <div className="space-y-4">
      <div className={`rounded-xl border p-4 ${urgent ? 'bg-critical/10 border-critical/50' : 'bg-warning/10 border-warning/50'}`}
        role="alert">
        <p className="flex items-center gap-2 font-semibold">
          <span aria-hidden="true">{urgent ? '⚠' : 'ⓘ'}</span>
          {urgent ? 'Seek urgent medical attention' : 'Worth discussing with a doctor'}
        </p>
        {result.flags.map((flag) => (
          <p key={flag.rule_id} className="text-sm mt-2 leading-relaxed">{flag.message}</p>
        ))}
      </div>

      <p className="text-sm text-ink-2">
        Your check-in has been saved. {urgent && 'Please do not wait on this experiment.'}
      </p>

      <Button variant="primary" full onClick={() => navigate('/')}>Back to today</Button>
    </div>
  );
}
